import React, { useEffect, useState } from "react";
import { X, Check, Sparkles } from "lucide-react";
import api from "../lib/api.js";
import { toast } from "sonner";

/**
 * Modal that lets the user pick which Posture Coach persona will speak to them
 * across chat, daily nudge and the sidelines companion.
 */
export default function CoachPickerModal({ open, onClose, onSelected }) {
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/coach/personas").then((r) => setData(r.data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const choose = async (persona) => {
    setPicking(persona.id);
    try {
      await api.post("/coach/persona", { persona_id: persona.id });
      toast.success(`${persona.name} is now your coach`);
      setData((d) => ({ ...d, selected_id: persona.id }));
      onSelected?.(persona);
      setTimeout(onClose, 600);
    } catch (e) {
      toast.error("Could not switch coach");
    } finally {
      setPicking(null);
    }
  };

  const personas = data?.personas || [];
  const selectedId = data?.selected_id;

  return (
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm ps-fade-up pointer-events-auto" data-testid="coach-picker">
      <div className="ps-card-elev p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/5" data-testid="coach-picker-close">
          <X size={18} className="text-slate-400" />
        </button>
        <div className="ps-chip mb-2"><Sparkles size={11} /> Choose your coach</div>
        <h2 className="ps-heading text-2xl font-extrabold">
          Pick a <span className="ps-serif ps-italic font-normal">voice.</span>
        </h2>
        <p className="text-xs text-slate-400 mt-1 ps-serif ps-italic">
          You can switch any time. Your choice shapes nudges, chat and sidelines tips.
        </p>

        {!data ? (
          <div className="mt-6 text-slate-400 text-sm ps-serif ps-italic">Loading…</div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-3">
            {personas.map((p) => (
              <button
                key={p.id}
                onClick={() => choose(p)}
                disabled={picking === p.id}
                data-testid={`coach-picker-${p.id}`}
                className="text-left ps-glass-card p-4 relative overflow-hidden flex gap-3 items-start transition-all w-full"
                style={{
                  boxShadow:
                    selectedId === p.id
                      ? `0 0 0 2px ${p.accent}, 0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 18px 40px -22px ${p.accent}aa`
                      : `0 1px 0 0 rgba(255,255,255,0.08) inset, 0 -1px 0 0 rgba(0,0,0,0.25) inset, 0 18px 40px -22px ${p.accent}66`,
                }}
              >
                {/* Ambient mesh tinted by persona accent */}
                <div aria-hidden className="ps-mesh-drift absolute inset-[-30%] opacity-50 pointer-events-none" style={{ background: `radial-gradient(circle at 20% 20%, ${p.accent}45 0%, transparent 45%), radial-gradient(circle at 80% 80%, ${p.accent}25 0%, transparent 50%)`, filter: "blur(20px)" }} />
                {/* Avatar */}
                <div
                  className="relative shrink-0 rounded-full overflow-hidden"
                  style={{
                    width: 64, height: 64,
                    border: `2px solid ${p.accent}66`,
                    boxShadow: `inset 0 1.5px 0 rgba(255,255,255,0.3), 0 12px 28px -8px ${p.accent}77`,
                  }}
                >
                  <img
                    src={p.avatar_url}
                    alt={p.name}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="w-full h-full object-cover"
                  />
                  {/* Fallback initials */}
                  <div className="absolute inset-0 flex items-center justify-center text-2xl font-extrabold ps-heading -z-10" style={{ background: `linear-gradient(160deg, ${p.accent}55 0%, ${p.accent}10 100%)`, color: p.accent }}>
                    {p.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                  </div>
                </div>
                {/* Body */}
                <div className="relative flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="ps-heading text-base font-extrabold leading-tight">{p.name}</div>
                    {selectedId === p.id && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest" style={{ background: `${p.accent}22`, color: p.accent, border: `1px solid ${p.accent}55` }}>
                        <Check size={10} /> Yours
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: p.accent }}>{p.role}</div>
                  <div className="text-[12px] text-slate-300 ps-serif ps-italic mt-1 leading-snug">"{p.signature}"</div>
                  <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">{p.tagline}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
